<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">You will need:</p>
    <ul class="default-list">
      <li>Hairdressing Shears (these can be bought very cheaply online)</li>
      <li>Cutting Comb (any straight-edged comb with teeth divided into 2 sections)</li>
      <li>Wig (a cheap one from a discount shop will serve well)</li>
    </ul>
    <h4 class="mb-5">Shears</h4>
    <p class="mb-5">
      Two of the most important tools that a hairdresser needs to master are the shears
      (hairdressing scissors) and the comb.
    </p>
    <p class="mb-5">
      These two tools are often used in combination when giving a client a haircut. While it can
      take lots of time and practice to gain mastery, you can learn some of the basics of their use
      right now. Enough to get started with, at least!
    </p>
    <p class="mb-5">First, let’s take a look at a hairdresser’s shear and its component parts.</p>

    <centered-image-wrapper
      :image="images[1]"
      classes="xl:w-1/2 lg:w-2/3 w-full"
      alt="Anatomy of a hair cutting shear: labelled parts are the tips or points, edge, inside cutting edge, outside blade, hollow ground, back side, pivot area, tension knob (dial or screw), pivot ride or balance face, leaf-spring plate, handle shank, handle neck, stopper, outside ring, sizing ring or insert, thumb ring, finger ring, and finger rest or tang."
    />

    <h4 class="mb-5">Cutting Comb</h4>
    <p class="mb-5">
      And now, let’s take a look at the cutting comb. There are many different combs and brushes
      that a hairdresser will use for a wide variety of jobs. Cutting combs come in many different
      sizes, but they are all designed to assist the hairdresser when cutting hair.
    </p>
    <p class="mb-5">
      Known for their straight edges that help achieve a clean cut, cutting combs have a row of
      teeth divided into two sections. In one section, the teeth are tightly packed together, and in
      the other, they’re slightly more spaced out. The first is for fine detailing, the second for
      rough work.
    </p>
    <centered-image-wrapper
      :image="images[2]"
      classes="xl:w-1/2 lg:w-2/3 w-full"
      alt="A black cutting comb with finely spaced teeth along one half and wider-spaced teeth along the other."
    />

    <h4 class="mb-5">How to Hold Hairdressing Shears</h4>
    <p class="mb-5">
      It’s time to learn how to hold your hairdressing shears. You must develop the correct
      technique from the outset as it can be difficult to correct poor technique once it has become
      a habit.
    </p>
    <p class="mb-5">There are two main reasons why you need to hold the shears correctly:</p>

    <ol class="numbered-list">
      <li>It enables accuracy and precision when cutting hair.</li>
      <li>
        It minimises strain on the hand and wrist and helps prevent repetitive strain injuries such
        as carpal tunnel syndrome.
      </li>
    </ol>
    <p class="mb-5">Now, watch the following video to learn how to hold them correctly:</p>

    <responsive-youtube-wrapper :video="videos[1]" :image="videoTrigger1" />
    <ol class="numbered-list">
      <li>
        Take some time to practise. Pick up your shears and secure the proper grip, as detailed in
        the picture below.
      </li>
      <li>
        Then practise opening and closing the shears in a cutting action using the independent thumb
        action described in the video.
      </li>
      <li>After that, replace the shears on the table again and go back to Step 1.</li>
    </ol>
    <p class="mb-5">
      Perform these steps repeatedly for a few minutes each day to help make your grip and cut
      become automatic.
    </p>
    <centered-image-wrapper
      :image="images[3]"
      classes="xl:w-1/2 lg:w-2/3 w-full"
      alt="How to hold your shears, traditional or Western grip: insert your ring finger through the smaller hole (the one with the tang) and your thumb through the larger hole; rest your middle and index fingers along the handle behind the blades; relax your hand and open and close the shears by moving your thumb only."
    />

    <h4 class="mb-5">How to Hold Shears and Comb in One Hand</h4>
    <p class="mb-5">
      There are many different ways to hold your shears and comb while you are cutting hair. The
      following technique takes a LOT of practice to master, but it’s a lot of fun too!
    </p>
    <p class="mb-5">
      When you master this technique, you’ll not only be much more efficient with cutting hair;
      you’ll also look incredibly cool while doing so! Just be careful - you don’t want to give
      yourself a nasty nip with the business end of your shears while learning! Slow and steady to
      start with is the best policy.
    </p>
    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5 text-center">
        <responsive-iframe-youtube :src="videos[2]" :posterimg="videoTrigger2" />
      </div>
    </div>

    <h4 class="mb-5">Cutting Skills</h4>
    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <p class="mb-5">
          Take a look at the PDF to learn more about cutting hair (click the image on the right to
          download it). Choose some cuts to practise on your wig. It’s best to start with long cuts
          first, as you won’t be able to do a long cut if you’ve already cut the hair short.
        </p>
        <p class="mb-5">
          Have fun, and remember, cutting a wig is a great place to make your mistakes without
          worry. The stakes are much higher with a living, breathing, and paying customer!
        </p>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5 text-center">
        <a :href="'content/hairdressing/cutting_skills.pdf' | blob_url" target="_blank"
          ><img
            :src="images[4]"
            alt="Read the cutting skills guide (PDF, opens in a new tab)"
            class="img-responsive my-auto"
        /></a>
      </div>
    </div>

    <p class="mb-5">
      <strong
        >NOTE: Take a before shot of your wig then when you do any of these cuts remember to
        photograph them all so you can upload your best work below.</strong
      >
    </p>

    <div v-for="index in 2" :key="index">
      <p class="mb-5">{{ file_titles[index] }}</p>
      <fileupload-button-immediate
        v-model="uploaded_files['uploaded_file_' + index]"
        :is-editable="isEditable"
        filetype="media"
        :index="index"
      />
    </div>
    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      images: {
        1: require('@/assets/images/experiences/hairdressing/activity61.png'),
        2: require('@/assets/images/experiences/hairdressing/activity62.png'),
        3: require('@/assets/images/experiences/hairdressing/activity63.png'),
        4: require('@/assets/images/experiences/hairdressing/activity64.png'),
      },
      videoTrigger1: require('@/assets/images/experiences/hairdressing/holding_scissors_splash.jpg'),
      videoTrigger2: require('@/assets/images/experiences/hairdressing/scissors_comb_one_hand.jpg'),
      videos: {
        1: 'https://www.youtube.com/embed/Izv0j7VG0Is',
        2:
          $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
          '/content/hairdressing/scissors_comb_one_hand.mp4',
      },
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
      },
      file_titles: {
        1: 'My before photograph upload',
        2: 'My after photograph upload',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>

<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
}
.correct {
  background: rgba(75, 202, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}

.workplace_module {
  overflow: hidden;
  padding: 10px 0;
  border-bottom: 1px solid #dedede;
}
.workplace_module:last-child {
  border-bottom: none;
}
p.module_name {
  float: left;
  font-size: 16px;
  font-weight: 600;
  padding-top: 10px;
}
.prework_icon {
  float: right;
  width: 10%;
  text-align: center;
}
select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
}
</style>
